import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useSearchProfiles } from "@/hooks/queries/useSearchProfiles";
import type { SearchProfile } from "@/types/domain";

const STORAGE_KEY = "licitigo.activeProfileId";

interface ActiveProfileContextValue {
  profiles: SearchProfile[];
  isLoading: boolean;
  activeProfile: SearchProfile | null;
  activeProfileId: number | null;
  setActiveProfileId: (id: number) => void;
}

const ActiveProfileContext = createContext<ActiveProfileContextValue | null>(
  null,
);

export function ActiveProfileProvider({ children }: { children: ReactNode }) {
  const { data: profiles = [], isLoading } = useSearchProfiles();
  const [activeProfileId, setActiveProfileIdState] = useState<number | null>(
    () => {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? Number(stored) : null;
    },
  );

  // Keep selection valid: default to first profile if none / stale.
  useEffect(() => {
    if (profiles.length === 0) return;
    const exists = profiles.some(
      (p) => p.searchprofile_id === activeProfileId,
    );
    if (!exists) {
      setActiveProfileIdState(profiles[0]!.searchprofile_id);
    }
  }, [profiles, activeProfileId]);

  const setActiveProfileId = (id: number) => {
    localStorage.setItem(STORAGE_KEY, String(id));
    setActiveProfileIdState(id);
  };

  useEffect(() => {
    if (activeProfileId != null) {
      localStorage.setItem(STORAGE_KEY, String(activeProfileId));
    }
  }, [activeProfileId]);

  const value = useMemo<ActiveProfileContextValue>(() => {
    const activeProfile =
      profiles.find((p) => p.searchprofile_id === activeProfileId) ??
      profiles[0] ??
      null;
    return {
      profiles,
      isLoading,
      activeProfile,
      activeProfileId: activeProfile?.searchprofile_id ?? null,
      setActiveProfileId,
    };
  }, [profiles, isLoading, activeProfileId]);

  return (
    <ActiveProfileContext.Provider value={value}>
      {children}
    </ActiveProfileContext.Provider>
  );
}

export function useActiveProfile() {
  const ctx = useContext(ActiveProfileContext);
  if (!ctx) {
    throw new Error(
      "useActiveProfile must be used within an ActiveProfileProvider",
    );
  }
  return ctx;
}
